<template>
  <WdsModal :show="show" no-close-icon :title="title" :disable-click-outside="true">
    <template #header>
      <WdsStack direction-column>
        <WdsHeading as="h1" type="title3">{{ title }}</WdsHeading>
        <WdsText v-if="description" secondary>
          {{ description }}
        </WdsText>
      </WdsStack>
    </template>
    <FieldableFormCustomFields
      v-if="paymentRequestStore.companyId && show"
      v-model:field-values="timesheetLinesStore.liveStateOfStagedTimesheetLine.customFieldValues"
      :account-id="paymentRequestStore.companyId"
      :account-ids="paymentRequestStore.customFieldProviderAccountIds"
      :fieldable-type="TypeSupportingCustomFieldValues.PaymentRequest"
      :context-message="
        $t(
          'Fill in the fields that describe where time should be applied. You can complete one or more fields for this line.',
        )
      "
      should-display-errors
      force-enable-inputs
    />
    <template #footer>
      <div class="tw-flex tw-justify-between tw-items-center">
        <div class="tw-flex tw-gap-md tw-items-center">
          <WdsButton secondary @click="emit('cancel')">Cancel</WdsButton>
          <WdsButtonGhost
            class="tw-flex tw-items-center tw-gap-sm"
            data-test="modal-timesheet-line-delete-button"
            @click="deleteLine"
          >
            Delete
            <WdsIcon :size="16" name="trash-can" />
          </WdsButtonGhost>
        </div>
        <div class="tw-flex tw-gap-md tw-items-center">
          <div class="tw-flex tw-gap-sm tw-items-center">
            <WdsTooltip
              :text="
                $t(
                  'All hours are payable by default. If you want to track unpaid hours, turn this toggle off.',
                )
              "
            >
              {{ $t('Payable') }}
            </WdsTooltip>
            <WdsToggle
              v-model:checked="timesheetLinesStore.liveStateOfStagedTimesheetLine.isPayable"
              data-test="modal-timesheet-line-payable-toggle"
            ></WdsToggle>
          </div>
          <WdsButton
            primary
            data-test="modal-timesheet-line-save-button"
            @click="saveLine"
          >
            Save
          </WdsButton>
        </div>
      </div>
    </template>
  </WdsModal>
</template>

<script setup lang="ts">
import { nextTick } from 'vue'
import {
  WdsButton,
  WdsButtonGhost,
  WdsHeading,
  WdsIcon,
  WdsModal,
  WdsStack,
  WdsText,
  WdsToggle,
  WdsTooltip,
} from '@worksome/wds-components'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import FieldableFormCustomFields from '@/components/Fieldable/FieldableFormCustomFields.vue'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useTimesheetLinesStore } from '@/components/Timesheet/store/useTimesheetLinesStore'
import { useTranslation } from '@/composables/translation'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'

const emit = defineEmits(['cancel', 'add-or-update-line'])

const props = defineProps<{
  show: boolean
  title?: string
  description: string
  primaryButtonText: string
  secondaryButtonText: string
}>()

const { $t } = useTranslation()
const timesheetLinesStore = useTimesheetLinesStore()
const paymentRequestStore = usePaymentRequestStore()
const fieldableFormStore = useFieldableFormStore()

const saveLine = async () => {
  await nextTick()
  await timesheetLinesStore.commitStagedTimesheetLine()
  fieldableFormStore.v$.$reset()
  emit('cancel')
}
const deleteLine = async () => {
  await timesheetLinesStore.deleteStagedTimesheetLine()
  fieldableFormStore.v$.$reset()
  emit('cancel')
}
</script>

<style scoped lang="scss">
.modal-confirmation {
  :deep(.wds-modal-content) {
    @apply tw-mt-10;
  }
  :deep(.wds-modal-body) {
    @apply tw-mt-0;
  }
}
</style>
